import { useMutation } from '@tanstack/react-query';

import { useProjectsApi } from 'api/data/projects/projects-api';
import { resolve } from 'api/resolve';
import { SNACKBAR_STORE } from 'state/snackbar/snackbarStore';

import { planMoveOperations } from '../utils/planMoveOperations';
import { useDecompositionRelation } from './useDecompositionRelation';

/**
 * Moves a node by changing its parent relation.
 *
 *  - New parent (cases 1 & 2): a single `addRelation`. The decomposable
 *    relation strategy on the backend removes the node's existing parent
 *    relation when the new one is created, so no explicit delete is needed.
 *  - Move to root (case 3): a single `deleteRelation` of the old parent.
 */
export function useMoveNode(projectId: string, templateNodeId: string, proposalId?: string) {
  const { addRelation, deleteRelation } = useProjectsApi();
  const { relationId } = useDecompositionRelation(templateNodeId);

  return useMutation({
    mutationFn: async ({
      nodeDataNodeId,
      oldParentDataNodeId,
      newParentDataNodeId
    }: {
      nodeDataNodeId: string;
      oldParentDataNodeId: string | null;
      newParentDataNodeId: string | null;
    }) => {
      const { shouldAdd, shouldDelete } = planMoveOperations({ oldParentDataNodeId, newParentDataNodeId });

      if (shouldAdd && newParentDataNodeId) {
        return await resolve(addRelation(projectId, nodeDataNodeId, newParentDataNodeId, relationId, proposalId));
      }
      if (shouldDelete && oldParentDataNodeId) {
        return await resolve(deleteRelation(projectId, nodeDataNodeId, relationId, oldParentDataNodeId, proposalId));
      }
      return undefined;
    },
    onError: () => {
      SNACKBAR_STORE.getState().showSnackbar('Failed to move node', 'error', true);
    }
  });
}
